import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { Calculator, BookOpen, Globe, FlaskConical, Clock, MapPin, Monitor, Leaf } from 'lucide-react';

const SUBJECTS = [
  { id: 'matematik', icon: Calculator, color: '#E85D3A', titleKey: 'subjectMath', type: 'math' },
  { id: 'bahasa-melayu', icon: BookOpen, color: '#2196F3', titleKey: 'subjectBM', type: 'mcq' },
  { id: 'english', icon: Globe, color: '#E85D3A', titleKey: 'subjectEN', type: 'mcq' },
  { id: 'sains', icon: FlaskConical, color: '#009688', titleKey: 'subjectScience', type: 'mcq' },
  { id: 'sejarah', icon: Clock, color: '#2196F3', titleKey: 'subjectHistory', type: 'mcq' },
  { id: 'geografi', icon: MapPin, color: '#4CAF50', titleKey: 'subjectGeography', type: 'mcq' },
  { id: 'informatik', icon: Monitor, color: '#8BC34A', titleKey: 'subjectIT', type: 'mcq' },
  { id: 'biologi', icon: Leaf, color: '#7CB342', titleKey: 'subjectBiology', type: 'mcq' },
];

const OPERATION_LIST = [
  { key: '+', labelKey: 'addition' },
  { key: '-', labelKey: 'subtraction' },
  { key: '\u00d7', labelKey: 'multiplication' },
  { key: '\u00f7', labelKey: 'division' },
];
const DURATIONS = [30, 45, 60, 90, 120, 180];

export default function PracticeSetupPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('matematik');
  const [difficulty, setDifficulty] = useState('easy');
  const [operations, setOperations] = useState(['+']);
  const [duration, setDuration] = useState(60);

  const currentSubject = SUBJECTS.find(s => s.id === selectedSubject);
  const isMath = currentSubject?.type === 'math';

  const toggleOp = (op) => {
    setOperations(prev => {
      if (prev.includes(op)) return prev.length === 1 ? prev : prev.filter(o => o !== op);
      return [...prev, op];
    });
  };

  const start = () => {
    navigate('/practice', {
      state: {
        playerName: playerName || 'Player',
        difficulty,
        operations,
        duration,
        subjectId: selectedSubject,
        subjectType: currentSubject?.type || 'math',
        language,
      }
    });
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{t('practiceMode', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        <motion.div className="setup-content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="setup-title">{t('practiceSetup', language)}</h1>

          {/* Player Name */}
          <div className="setup-section">
            <h3 className="setup-section-title">{t('playerName', language)}</h3>
            <input className="setup-input setup-input-team1" type="text" value={playerName}
              onChange={e => setPlayerName(e.target.value)} placeholder={t('enterName', language)}
              maxLength={15} data-testid="practice-name-input" />
          </div>

          {/* Subject Selection */}
          <div className="setup-section">
            <h3 className="setup-section-title">{t('chooseSubject', language)}</h3>
            <div className="practice-subject-grid" data-testid="practice-subject-grid">
              {SUBJECTS.map((subject) => {
                const Icon = subject.icon;
                const isSelected = selectedSubject === subject.id;
                return (
                  <button
                    key={subject.id}
                    className={`practice-subject-btn ${isSelected ? 'practice-subject-active' : ''}`}
                    style={isSelected ? { background: subject.color, color: 'white', borderColor: subject.color } : {}}
                    onClick={() => setSelectedSubject(subject.id)}
                    data-testid={`practice-subject-${subject.id}`}
                  >
                    <Icon size={18} />
                    <span>{t(subject.titleKey, language)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Difficulty */}
          <div className="setup-section">
            <h3 className="setup-section-title">{t('difficulty', language)}</h3>
            <div className="setup-tabs">
              <button className={`setup-tab ${difficulty === 'easy' ? 'setup-tab-active' : ''}`}
                onClick={() => setDifficulty('easy')}>
                {t('easy', language)}
                <span className="setup-tab-desc">{t('primaryLower', language)}</span>
              </button>
              <button className={`setup-tab ${difficulty === 'medium' ? 'setup-tab-active' : ''}`}
                onClick={() => setDifficulty('medium')}>
                {t('medium', language)}
                <span className="setup-tab-desc">{t('primaryUpper', language)}</span>
              </button>
              <button className={`setup-tab ${difficulty === 'hard' ? 'setup-tab-active setup-tab-hard' : ''}`}
                onClick={() => setDifficulty('hard')}>
                {t('hard', language)}
                <span className="setup-tab-desc">{t('secondary', language)}</span>
              </button>
            </div>
          </div>

          {/* Operations - Math only */}
          {isMath && (
            <div className="setup-section">
              <h3 className="setup-section-title">{t('operations', language)}</h3>
              <div className="setup-operations">
                {OPERATION_LIST.map(op => (
                  <button key={op.key} className={`setup-op-btn ${operations.includes(op.key) ? 'setup-op-active' : ''}`}
                    onClick={() => toggleOp(op.key)}>
                    <span className="setup-op-symbol">{op.key}</span>
                    <span className="setup-op-label">{t(op.labelKey, language)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Duration */}
          <div className="setup-section">
            <h3 className="setup-section-title">{t('duration', language)}</h3>
            <div className="setup-durations">
              {DURATIONS.map(d => (
                <button key={d} className={`setup-duration-btn ${duration === d ? 'setup-duration-active' : ''}`}
                  onClick={() => setDuration(d)}>{d} {t('seconds', language)}</button>
              ))}
            </div>
          </div>

          <motion.button className="setup-start-btn" onClick={start}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid="practice-start-button">
            {t('startPractice', language)}
          </motion.button>
          <button className="setup-back-btn" onClick={() => navigate('/')}>&larr; {t('backToHome', language)}</button>
        </motion.div>
      </div>
    </div>
  );
}
